'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { CalendarIcon, ClockIcon, UsersIcon, TrophyIcon, PlayIcon } from '@/components/Icons'

interface Contest {
  id: string
  title: string
  description: string
  startTime: string
  endTime: string
  duration: number
  status: string
  maxPoints: number
  _count: {
    ranks: number
  }
}

export default function ContestsPage() {
  const [contests, setContests] = useState<Contest[]>([])
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'running' | 'finished'>('all')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchContests()
  }, [filter])

  const fetchContests = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`/api/contests?status=${filter}`)
      const data = await response.json()
      if (response.ok) {
        setContests(data)
      }
    } catch (error) {
      console.error('Error fetching contests:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'upcoming':
        return 'bg-blue-900/30 text-blue-300 border border-blue-500/30'
      case 'running':
        return 'bg-green-900/30 text-green-300 border border-green-500/30'
      case 'finished':
        return 'bg-gray-700/30 text-gray-300 border border-gray-500/30'
      default:
        return 'bg-gray-700/30 text-gray-300 border border-gray-500/30'
    }
  }

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
  }

  const isContestActive = (contest: Contest) => {
    const now = new Date()
    const start = new Date(contest.startTime)
    const end = new Date(contest.endTime)
    return now >= start && now <= end
  }

  const canJoin = (contest: Contest) => {
    const now = new Date()
    const start = new Date(contest.startTime)
    return now >= start && contest.status === 'Running'
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="bg-dark-700 h-8 w-64 rounded mb-8"></div>
          <div className="space-y-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-dark-700 h-32 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold glow-text mb-4">Math Contests</h1>
        <p className="text-gray-200 mb-6">
          Participate in mathematics contests and compete with others
        </p>

        <div className="flex gap-2 mb-6 overflow-x-auto">
          {(['all', 'upcoming', 'running', 'finished'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-md font-medium capitalize whitespace-nowrap ${
                filter === status
                  ? 'btn-primary'
                  : 'btn-secondary'
              }`}
            >
              {status === 'all' ? 'All Contests' : status}
            </button>
          ))}
        </div>
      </div>

      {contests.length === 0 ? (
        <div className="text-center py-12">
          <TrophyIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No contests found</p>
          <p className="text-gray-400">
            {filter === 'all' ? 'Check back later for new contests!' : `No ${filter} contests at the moment`}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {contests.map((contest) => (
            <div key={contest.id} className="card-energy p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-bold text-white">
                      {contest.title}
                    </h2>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contest.status)}`}>
                      {contest.status}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">{contest.description}</p>
                </div>

                <div className="flex items-center gap-3 ml-4">
                  {canJoin(contest) ? (
                    <Link
                      href={`/contests/${contest.id}`}
                      className="btn btn-primary flex items-center space-x-1"
                    >
                      <PlayIcon className="w-4 h-4" />
                      <span>Join Contest</span>
                    </Link>
                  ) : contest.status === 'Finished' ? (
                    <Link
                      href={`/contests/${contest.id}`}
                      className="btn btn-secondary flex items-center space-x-1"
                    >
                      <TrophyIcon className="w-4 h-4" />
                      <span>View Results</span>
                    </Link>
                  ) : (
                    <Link
                      href={`/contests/${contest.id}`}
                      className="btn btn-secondary"
                    >
                      View Details
                    </Link>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center space-x-2 text-gray-600">
                  <CalendarIcon className="w-4 h-4" />
                  <div>
                    <div className="font-medium">Start Time</div>
                    <div>{new Date(contest.startTime).toLocaleString()}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-gray-600">
                  <ClockIcon className="w-4 h-4" />
                  <div>
                    <div className="font-medium">Duration</div>
                    <div>{formatDuration(contest.duration)}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-gray-600">
                  <UsersIcon className="w-4 h-4" />
                  <div>
                    <div className="font-medium">Participants</div>
                    <div>{contest._count.ranks} registered</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-gray-600">
                  <TrophyIcon className="w-4 h-4" />
                  <div>
                    <div className="font-medium">Max Points</div>
                    <div>{contest.maxPoints || 'TBD'}</div>
                  </div>
                </div>
              </div>

              {isContestActive(contest) && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-green-800 font-medium">
                      🔴 Contest is currently running!
                    </span>
                    <span className="text-green-600 text-sm">
                      Ends: {new Date(contest.endTime).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        <div className="card-energy p-6 text-center">
          <span className="text-5xl text-blue-400 mx-auto mb-4 block">📅</span>
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Weekly Contests</h3>
          <p className="text-gray-200 text-sm">
            Weekly contests to test your math skills and compete with others.
          </p>
        </div>
        
        <div className="card-energy p-6 text-center">
          <span className="text-5xl text-yellow-400 mx-auto mb-4 block">🏆</span>
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Rating Changes</h3>
          <p className="text-gray-200 text-sm">
            Your rating will change based on your performance in contests.
          </p>
        </div>
        
        <div className="card-energy p-6 text-center">
          <span className="text-5xl text-green-400 mx-auto mb-4 block">👥</span>
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Global Competition</h3>
          <p className="text-gray-200 text-sm">
            Compete with mathematicians from around the world.
          </p>
        </div>
      </div>
    </div>
  )
}